/**
 * Monats-Mini-Map für den "Mein Kalender"-Tab.
 * 
 * Kompakte 2-Monats-Vorschau (aktueller + nächster Monat) mit farbcodierten
 * Tagen — auf einen Blick sieht der Mitarbeiter, wann Einsätze, Urlaube oder
 * Abwesenheiten anstehen, ohne in den Einsatzplan oder Team-Kalender zu wechseln.
 * 
 * Farbcodierung (Priorität: Krankheit > Elternzeit > Urlaub > Einsatz):
 *  • Rot      = Krankheit / KO
 *  • Blau     = Elternzeit
 *  • Orange   = Kind krank
 *  • Grün     = Genehmigter Urlaub
 *  • Gelb     = Beantragter Urlaub
 *  • Primary  = Einsatz
 *  • Grau     = Heute
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  startOfMonth, endOfMonth, eachDayOfInterval, format, addMonths,
  startOfWeek, endOfWeek, isSameMonth, isSameDay, parseISO, getISOWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';
import { Briefcase, Sparkles, Clock, MapPin, DoorOpen, Calendar, FileText, Activity, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { icalAPI } from '../../lib/api';

const WEEKDAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

// Mapping Status → Tailwind-Klassen + Label
const STATUS_STYLES = {
  sick:        { bg: 'bg-red-500',    text: 'text-white', label: 'Krankheit' },
  sick_no_note:{ bg: 'bg-red-400',    text: 'text-white', label: 'Krank o. Schein' },
  child_sick:  { bg: 'bg-orange-500', text: 'text-white', label: 'Kind krank' },
  parental:    { bg: 'bg-blue-500',   text: 'text-white', label: 'Elternzeit' },
  vacation:    { bg: 'bg-emerald-500', text: 'text-white', label: 'Urlaub' },
  vacation_pending: { bg: 'bg-yellow-400', text: 'text-yellow-950', label: 'Urlaub (offen)' },
  shift:       { bg: 'bg-primary',    text: 'text-primary-foreground', label: 'Einsatz' },
  holiday:     { bg: 'bg-purple-200 dark:bg-purple-900/50', text: 'text-purple-900 dark:text-purple-100', label: 'Feiertag' },
  none:        { bg: 'bg-muted/30',   text: 'text-muted-foreground', label: '–' },
};

const ABSENCE_TYPE_TO_STATUS = {
  K: 'sick',
  KO: 'sick_no_note',
  KK: 'child_sick',
  EZ: 'parental',
};

export const MyCalendarMiniMap = () => {
  const [events, setEvents] = useState({ shifts: [], vacations: [], absences: [], holidays: [] });
  const [loading, setLoading] = useState(true);

  // Zeitfenster: heute → +60 Tage (≈ 2 Monate)
  const [anchor] = useState(() => new Date());
  const months = useMemo(() => [anchor, addMonths(anchor, 1)], [anchor]);

  useEffect(() => {
    const start = startOfMonth(months[0]);
    const end = endOfMonth(months[1]);
    setLoading(true);
    icalAPI
      .getMyCalendarEvents(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'))
      .then(({ data }) => setEvents({
        shifts: data?.shifts || [],
        vacations: data?.vacations || [],
        absences: data?.absences || [],
        holidays: data?.holidays || [],
      }))
      .catch((err) => {
        console.error('Mini-Map konnte nicht geladen werden', err);
        setEvents({ shifts: [], vacations: [], absences: [], holidays: [] });
      })
      .finally(() => setLoading(false));
  }, [months]);

  /** Liefert für einen Tag den höchstprioritären Status + alle Events. */
  const dayData = useMemo(() => {
    const map = {}; // dateStr → { primary: {kind, meta}, all: [{kind, meta}] }

    const addEvent = (dateStr, kind, meta, priority) => {
      if (!map[dateStr]) map[dateStr] = { primary: null, primaryPrio: -Infinity, all: [] };
      map[dateStr].all.push({ kind, meta });
      if (priority > map[dateStr].primaryPrio) {
        map[dateStr].primary = { kind, meta };
        map[dateStr].primaryPrio = priority;
      }
    };

    // 0. Feiertage (niedrigste Prio = -1, jedes andere Event ueberschreibt)
    events.holidays.forEach((h) => {
      addEvent(h.date, 'holiday', h, -1);
    });
    // 1. Shifts (Prio = 0)
    events.shifts.forEach((s) => {
      addEvent(s.date, 'shift', s, 0);
    });
    // 2. Pending Vacations (Prio 1)
    events.vacations
      .filter((v) => v.status === 'pending')
      .forEach((v) => {
        eachDayOfInterval({ start: parseISO(v.start_date), end: parseISO(v.end_date) }).forEach((d) => {
          addEvent(format(d, 'yyyy-MM-dd'), 'vacation_pending', v, 1);
        });
      });
    // 3. Approved Vacations (Prio 2)
    events.vacations
      .filter((v) => v.status === 'approved')
      .forEach((v) => {
        eachDayOfInterval({ start: parseISO(v.start_date), end: parseISO(v.end_date) }).forEach((d) => {
          addEvent(format(d, 'yyyy-MM-dd'), 'vacation', v, 2);
        });
      });
    // 4. Absences (höchste Prio = 3)
    events.absences.forEach((a) => {
      const status = ABSENCE_TYPE_TO_STATUS[a.absence_type] || 'sick';
      eachDayOfInterval({ start: parseISO(a.start_date), end: parseISO(a.end_date) }).forEach((d) => {
        addEvent(format(d, 'yyyy-MM-dd'), status, a, 3);
      });
    });
    return map;
  }, [events]);

  const getDayStatus = (dateStr) => dayData[dateStr]?.primary || null;
  const getDayEvents = (dateStr) => dayData[dateStr]?.all || [];

  return (
    <Card data-testid="my-calendar-mini-map">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle>Auf einen Blick</CardTitle>
        </div>
        <CardDescription>
          Farbcodierte Übersicht über deine Einsätze, Urlaube und Abwesenheiten in den nächsten zwei Monaten.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Skeleton className="h-56 w-full" />
            <Skeleton className="h-56 w-full" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {months.map((monthDate) => (
              <MonthGrid
                key={monthDate.toISOString()}
                monthDate={monthDate}
                getDayStatus={getDayStatus}
                getDayEvents={getDayEvents}
              />
            ))}
          </div>
        )}

        {/* Legende */}
        <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs pt-2 border-t">
          {[
            ['shift', 'Einsatz'],
            ['vacation', 'Urlaub (genehmigt)'],
            ['vacation_pending', 'Urlaub (offen)'],
            ['sick', 'Krankheit'],
            ['child_sick', 'Kind krank'],
            ['parental', 'Elternzeit'],
            ['holiday', 'Feiertag'],
          ].map(([key, label]) => (
            <div key={key} className="flex items-center gap-1.5" data-testid={`mini-map-legend-${key}`}>
              <div className={`w-3 h-3 rounded-sm ${STATUS_STYLES[key].bg}`} />
              <span className="text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const MonthGrid = ({ monthDate, getDayStatus, getDayEvents }) => {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });
  const today = new Date();

  // Tage in Wochen gruppieren (jeweils 7 Tage)
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <div className="space-y-1.5" data-testid={`mini-map-${format(monthDate, 'yyyy-MM')}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm">{format(monthDate, 'MMMM yyyy', { locale: de })}</h4>
      </div>
      {/* Header: KW-Spalte + Wochentage */}
      <div className="grid grid-cols-[1.5rem_repeat(7,1fr)] gap-1 text-[10px] text-muted-foreground font-medium">
        <div className="text-center" title="Kalenderwoche">KW</div>
        {WEEKDAY_NAMES.map((wd) => (
          <div key={wd} className="text-center">{wd}</div>
        ))}
      </div>
      {/* Wochenzeilen mit KW + Tageszellen */}
      <div className="space-y-1">
        {weeks.map((week) => {
          const kw = getISOWeek(week[0]);
          const weekStartParam = format(week[0], 'yyyy-MM-dd');
          return (
            <div
              key={`kw-${weekStartParam}`}
              className="grid grid-cols-[1.5rem_repeat(7,1fr)] gap-1"
            >
              <Link
                to={`/einsatzplan?week=${weekStartParam}`}
                className="flex items-center justify-center text-[10px] font-medium text-muted-foreground/80 hover:text-primary hover:bg-primary/10 rounded transition-colors tabular-nums"
                data-testid={`mini-map-kw-${kw}`}
                title={`Kalenderwoche ${kw} im Einsatzplan oeffnen`}
                aria-label={`KW ${kw} im Einsatzplan oeffnen`}
              >
                {kw}
              </Link>
              {week.map((day) => (
                <DayCell
                  key={day.toISOString()}
                  day={day}
                  monthDate={monthDate}
                  today={today}
                  status={getDayStatus(format(day, 'yyyy-MM-dd'))}
                  allEvents={getDayEvents(format(day, 'yyyy-MM-dd'))}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DayCell = ({ day, monthDate, today, status, allEvents }) => {
  const dateStr = format(day, 'yyyy-MM-dd');
  const inMonth = isSameMonth(day, monthDate);
  const isToday = isSameDay(day, today);
  const styleKey = status?.kind || 'none';
  const hasEvents = allEvents.length > 0;

  const cellClass = `
    aspect-square flex items-center justify-center text-[11px] rounded
    ${inMonth ? '' : 'opacity-30'}
    ${status ? `${STATUS_STYLES[styleKey].bg} ${STATUS_STYLES[styleKey].text} font-medium` : 'bg-muted/30 text-muted-foreground'}
    ${isToday && !status ? 'ring-2 ring-primary' : ''}
    ${isToday && status ? 'ring-2 ring-foreground/40' : ''}
    transition-all hover:scale-105 hover:opacity-90
    ${hasEvents ? 'cursor-pointer hover:shadow-sm' : 'cursor-default'}
  `.trim().replace(/\s+/g, ' ');

  // Tage ohne Event sind nicht klickbar — direkt rendern
  if (!hasEvents) {
    return (
      <div
        title={format(day, 'dd.MM.yyyy', { locale: de })}
        data-testid={`mini-map-day-${dateStr}`}
        data-kind={styleKey}
        className={cellClass}
      >
        {format(day, 'd')}
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`mini-map-day-${dateStr}`}
          data-kind={styleKey}
          className={cellClass}
          aria-label={`${format(day, 'dd.MM.yyyy', { locale: de })}: ${STATUS_STYLES[styleKey].label}`}
        >
          {format(day, 'd')}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-80 p-0" data-testid={`mini-map-popover-${dateStr}`}>
        <DayDetail day={day} events={allEvents} />
      </PopoverContent>
    </Popover>
  );
};

const DayDetail = ({ day, events }) => {
  // Sortiere: Krankheit/Elternzeit zuerst, dann Urlaub, dann Einsätze, Feiertag zuletzt
  const sorted = [...events].sort((a, b) => {
    const orderMap = { sick: 0, sick_no_note: 0, parental: 1, child_sick: 1, vacation: 2, vacation_pending: 3, shift: 4, holiday: 5 };
    const oa = orderMap[a.kind] ?? 99;
    const ob = orderMap[b.kind] ?? 99;
    if (oa !== ob) return oa - ob;
    // Innerhalb gleicher Kategorie: Shifts nach start_time
    if (a.kind === 'shift' && b.kind === 'shift') {
      return (a.meta.start_time || '').localeCompare(b.meta.start_time || '');
    }
    return 0;
  });

  return (
    <div className="divide-y" data-testid="mini-map-day-detail">
      <div className="px-4 py-3">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {format(day, 'EEEE', { locale: de })}
        </div>
        <div className="font-semibold text-base">
          {format(day, 'dd. MMMM yyyy', { locale: de })}
        </div>
      </div>
      <div className="px-2 py-2 space-y-1.5 max-h-80 overflow-y-auto">
        {sorted.map((ev, idx) => (
          <EventRow key={`${ev.kind}-${ev.meta.id}-${idx}`} kind={ev.kind} meta={ev.meta} />
        ))}
      </div>
      <div className="px-4 py-2 flex justify-end">
        {sorted.some((e) => e.kind === 'shift') ? (
          <Button asChild size="sm" variant="ghost" className="text-xs">
            <Link to="/einsatzplan">
              Im Einsatzplan öffnen
              <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        ) : sorted.some((e) => e.kind?.startsWith('vacation')) ? (
          <Button asChild size="sm" variant="ghost" className="text-xs">
            <Link to="/vacations">
              Zur Urlaubsplanung
              <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const EventRow = ({ kind, meta }) => {
  const style = STATUS_STYLES[kind] || STATUS_STYLES.none;

  if (kind === 'shift') {
    return (
      <div className="flex items-start gap-2 px-2 py-2 rounded hover:bg-muted/50">
        <div className={`w-1 self-stretch rounded-full ${style.bg}`} />
        <div className="flex-1 min-w-0 text-xs space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium">Einsatz</span>
            {meta.massnahme_short_code && (
              <Badge
                variant="outline"
                className="text-[10px] px-1 py-0 h-4"
                style={{ borderColor: meta.massnahme_color, color: meta.massnahme_color }}
              >
                {meta.massnahme_short_code}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-3 w-3" />
            {meta.start_time && meta.end_time ? `${meta.start_time} – ${meta.end_time}` : 'Ganztags'}
          </div>
          {meta.massnahme_name && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {meta.massnahme_name}
            </div>
          )}
          {meta.room_name && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <DoorOpen className="h-3 w-3" />
              {meta.room_name}
            </div>
          )}
          {meta.location_name && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {meta.location_name}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (kind === 'vacation' || kind === 'vacation_pending') {
    return (
      <div className="flex items-start gap-2 px-2 py-2 rounded hover:bg-muted/50">
        <div className={`w-1 self-stretch rounded-full ${style.bg}`} />
        <div className="flex-1 min-w-0 text-xs space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium">{style.label}</span>
            {meta.vacation_type && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                {meta.vacation_type}
              </Badge>
            )}
          </div>
          <div className="text-muted-foreground">
            {format(parseISO(meta.start_date), 'dd.MM.yyyy', { locale: de })}
            {meta.start_date !== meta.end_date && (
              <> – {format(parseISO(meta.end_date), 'dd.MM.yyyy', { locale: de })}</>
            )}
          </div>
          {meta.notes && (
            <div className="flex items-start gap-1 text-muted-foreground">
              <FileText className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span className="break-words">{meta.notes}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (kind === 'holiday') {
    return (
      <div className="flex items-start gap-2 px-2 py-2 rounded hover:bg-muted/50">
        <div className={`w-1 self-stretch rounded-full ${style.bg}`} />
        <div className="flex-1 min-w-0 text-xs space-y-0.5">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3 w-3" />
            <span className="font-medium">Feiertag</span>
            {meta.bundesland && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                {meta.bundesland}
              </Badge>
            )}
          </div>
          <div className="text-muted-foreground">{meta.name}</div>
        </div>
      </div>
    );
  }

  // Absences (sick / child_sick / parental / sick_no_note)
  return (
    <div className="flex items-start gap-2 px-2 py-2 rounded hover:bg-muted/50">
      <div className={`w-1 self-stretch rounded-full ${style.bg}`} />
      <div className="flex-1 min-w-0 text-xs space-y-0.5">
        <div className="flex items-center gap-2">
          <Activity className="h-3 w-3" />
          <span className="font-medium">{meta.absence_type_label || style.label}</span>
        </div>
        <div className="text-muted-foreground">
          {format(parseISO(meta.start_date), 'dd.MM.yyyy', { locale: de })}
          {meta.start_date !== meta.end_date && (
            <> – {format(parseISO(meta.end_date), 'dd.MM.yyyy', { locale: de })}</>
          )}
        </div>
      </div>
    </div>
  );
};
